'use client'

import { FieldValues, useForm } from 'react-hook-form'
import { useMemo, useState } from 'react'
import { CustomerInfoProps } from '@/root/shared-types/reservation/reservation-types'
import {
  emailValidationPattern,
  firstNameValidationPattern,
  lastNameValidationPattern,
  phoneValidationPattern,
  legalNameValidationPattern,
  registrationCodeValidationPattern,
  addressValidationPattern,
  zipValidationPattern,
  cityValidationPattern,
} from '@/root/shared-consts/regex-patterns/regexPatterns'
import { PersonFormFields, CompanyFormFields } from '@/root/modules/shared/ConfirmPage/const'
import type { CustomerType, FieldConfig } from '@/root/modules/shared/ConfirmPage/types'
import { useDefaultCountry } from '@/root/modules/shared/ConfirmPage/hooks/useDefaultCountry'
import { useGetTranslations } from '@/root/shared-hooks'

const REQUIRED_COMPANY_FIELDS = [
  CompanyFormFields.LEGAL_NAME,
  CompanyFormFields.REGISTRATION_CODE,
  CompanyFormFields.LEGAL_ADDRESS,
  CompanyFormFields.ZIP,
  CompanyFormFields.CITY,
  CompanyFormFields.COUNTRY,
]

interface UseCustomerFormProps {
  customerInfo: CustomerInfoProps
}

export const useCustomerForm = ({ customerInfo }: UseCustomerFormProps) => {
  const { getTranslation } = useGetTranslations()
  const { defaultCountryCode, defaultIntlCode } = useDefaultCountry()

  // Get initial values from customerInfo (pre-filled from API if available)
  const code = customerInfo.phoneNumber?.split(' ')[0] || defaultIntlCode
  const phoneNumber = customerInfo.phoneNumber?.split(' ')[1] || ''
  const { firstName = '', lastName = '', email = '', companyInfo } = customerInfo

  // Set initial customer type based on whether company info exists
  const hasCompanyInfo = Boolean(companyInfo?.legalName)
  const [customerType, setCustomerType] = useState<CustomerType>(hasCompanyInfo ? 'company' : 'private')

  const {
    register,
    watch,
    setValue,
    getValues,
    reset,
    trigger,
    formState: { errors },
    clearErrors,
  } = useForm<FieldValues>({
    mode: 'onBlur',
    defaultValues: {
      // Person fields - pre-filled from customerInfo
      [PersonFormFields.FIRST_NAME]: firstName,
      [PersonFormFields.LAST_NAME]: lastName,
      [PersonFormFields.INTL_CODE]: code,
      [PersonFormFields.PHONE_NUMBER]: phoneNumber,
      [PersonFormFields.EMAIL]: email,
      // Company fields - pre-filled from companyInfo if available
      [CompanyFormFields.LEGAL_NAME]: companyInfo?.legalName ?? '',
      [CompanyFormFields.REGISTRATION_CODE]: companyInfo?.registrationNumber ?? '',
      [CompanyFormFields.LEGAL_ADDRESS]: companyInfo?.address ?? '',
      [CompanyFormFields.ZIP]: companyInfo?.zip ?? '',
      [CompanyFormFields.CITY]: companyInfo?.city ?? '',
      [CompanyFormFields.COUNTRY]: companyInfo?.country ?? defaultCountryCode,
    },
  })

  // Person fields configuration
  const personFields: FieldConfig[] = useMemo(
    () => [
      {
        id: PersonFormFields.FIRST_NAME,
        label: getTranslation('confirmationContactFormFirstName'),
        type: 'text',
        placeholder: getTranslation('confirmationContactFormFirstNamePlaceHolder'),
        validationPattern: firstNameValidationPattern,
      },
      {
        id: PersonFormFields.LAST_NAME,
        label: getTranslation('confirmationContactFormLastName'),
        type: 'text',
        placeholder: getTranslation('confirmationContactFormLastNamePlaceHolder'),
        validationPattern: lastNameValidationPattern,
      },
      {
        id: PersonFormFields.PHONE_NUMBER,
        label: getTranslation('confirmationContactFormPhoneNumber'),
        type: 'tel',
        placeholder: getTranslation('confirmationContactFormPhonePlaceHolder'),
        validationPattern: phoneValidationPattern,
        isPhone: true,
      },
      {
        id: PersonFormFields.EMAIL,
        label: getTranslation('confirmationContactFormEmail'),
        type: 'email',
        placeholder: getTranslation('confirmationContactFormEmailPlaceHolder'),
        validationPattern: emailValidationPattern,
      },
    ],
    [getTranslation]
  )

  // Company fields configuration
  const companyFields: FieldConfig[] = useMemo(
    () => [
      {
        id: CompanyFormFields.LEGAL_NAME,
        label: getTranslation('confirmPageCompanyName'),
        type: 'text',
        validationPattern: legalNameValidationPattern,
      },
      {
        id: CompanyFormFields.REGISTRATION_CODE,
        label: getTranslation('confirmPageRegistrationCode'),
        type: 'text',
        validationPattern: registrationCodeValidationPattern,
      },
      {
        id: CompanyFormFields.LEGAL_ADDRESS,
        label: getTranslation('confirmPageLegalAddress'),
        type: 'text',
        validationPattern: addressValidationPattern,
        widthClass: 'flex-1',
      },
      {
        id: CompanyFormFields.ZIP,
        label: getTranslation('confirmPagePostalCode'),
        type: 'text',
        validationPattern: zipValidationPattern,
        widthClass: 'w-28',
      },
      {
        id: CompanyFormFields.CITY,
        label: getTranslation('confirmPageCity'),
        type: 'text',
        validationPattern: cityValidationPattern,
        widthClass: 'flex-1',
      },
      {
        id: CompanyFormFields.COUNTRY,
        label: getTranslation('confirmPageCountry'),
        type: 'text',
        isCountrySelect: true,
        widthClass: 'flex-1',
      },
    ],
    [getTranslation]
  )

  // Check if any company field has been filled
  const hasAnyCompanyField = () =>
    Object.values(CompanyFormFields).some((field) => {
      const value = getValues(field)
      return field === CompanyFormFields.COUNTRY ? value && value !== defaultCountryCode : value?.trim()
    })

  // Get company data for API
  const getCompanyData = () => {
    const values = getValues()
    return {
      legalName: values[CompanyFormFields.LEGAL_NAME],
      address: values[CompanyFormFields.LEGAL_ADDRESS],
      registrationNumber: values[CompanyFormFields.REGISTRATION_CODE],
      city: values[CompanyFormFields.CITY],
      zip: values[CompanyFormFields.ZIP],
      country: values[CompanyFormFields.COUNTRY],
      taxNumber: '',
      ruc: '',
      type: 'REGULAR' as const,
    }
  }

  // Get owner data for API
  const getOwnerData = () => {
    const values = getValues()
    // Include companyInfo when customer type is company OR when any company field is filled manually
    const includeCompany = customerType === 'company' || hasAnyCompanyField()
    return {
      firstName: values[PersonFormFields.FIRST_NAME],
      lastName: values[PersonFormFields.LAST_NAME],
      email: values[PersonFormFields.EMAIL],
      phone: {
        intlCode: values[PersonFormFields.INTL_CODE]?.replace('+', ''),
        phoneNumber: values[PersonFormFields.PHONE_NUMBER],
      },
      companyInfo: includeCompany ? getCompanyData() : undefined,
    }
  }

  // Trigger validation for relevant fields based on customer type
  // Also validates company fields when private user has started filling them
  const triggerValidation = async () => {
    const fieldsToValidate: string[] = Object.values(PersonFormFields)
    if (customerType === 'company' || hasAnyCompanyField()) {
      fieldsToValidate.push(...REQUIRED_COMPANY_FIELDS)
    }
    return trigger(fieldsToValidate)
  }

  // Validate only person fields (used for save-on-blur)
  const triggerPersonValidation = async () => {
    return trigger(Object.values(PersonFormFields) as string[])
  }

  return {
    // Form methods
    register,
    watch,
    setValue,
    reset,
    errors,
    clearErrors,
    // Field configurations
    personFields,
    companyFields,
    // Customer type
    customerType,
    setCustomerType,
    // Validation
    triggerValidation,
    triggerPersonValidation,
    // Data getters
    getOwnerData,
  }
}
